import { useEffect, useState } from 'react';

import { apiRequest } from '@/config';
import { logError } from '@/lib/logger';

import { ConfigSelector, type ModelConfig } from '../shared/ConfigSelector';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';

interface ExecuteSeedModalProps {
  runId: string;
  promptText: string;
  existingConfigIds: string[];
  onClose: () => void;
  onExecutionStarted: () => void;
}

export function ExecuteSeedModal({
  runId,
  promptText,
  existingConfigIds,
  onClose,
  onExecutionStarted,
}: ExecuteSeedModalProps) {
  const [configs, setConfigs] = useState<ModelConfig[]>([]);
  const [selectedConfigIds, setSelectedConfigIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch available configs
  useEffect(() => {
    async function fetchConfigs() {
      try {
        setLoading(true);
        const response = await apiRequest('/api/eval/configs');
        const allConfigs = response.configs || [];
        setConfigs(allConfigs);

        // Pre-select all enabled configs
        const enabledConfigs = allConfigs.filter(
          (c: ModelConfig) => c.enabled !== false
        );
        setSelectedConfigIds(enabledConfigs.map((c: ModelConfig) => c.id));
      } catch (err: any) {
        logError('Failed to fetch configs', err);
        setError(err.message || 'Failed to load model configs');
      } finally {
        setLoading(false);
      }
    }

    fetchConfigs();
  }, []);

  const handleExecute = async () => {
    if (selectedConfigIds.length === 0) {
      setError('Please select at least one model config');
      return;
    }

    // Check if any selected configs have existing results
    const hasExistingResults = selectedConfigIds.some(id =>
      existingConfigIds.includes(id)
    );

    try {
      setExecuting(true);
      setError(null);

      const response = await apiRequest(`/api/arena/runs/${runId}/execute`, {
        method: 'POST',
        body: JSON.stringify({
          config_ids: selectedConfigIds,
          replace: hasExistingResults,
        }),
      });

      if (response.success) {
        onExecutionStarted();
        onClose();
      } else {
        setError(response.error || 'Execution failed');
      }
    } catch (err: any) {
      logError('Failed to execute models', err);
      setError(err.message || 'Failed to start execution');
    } finally {
      setExecuting(false);
    }
  };

  // Count how many selected configs have existing results
  const selectedWithResults = selectedConfigIds.filter(id =>
    existingConfigIds.includes(id)
  ).length;

  return (
    <Modal
      title="Run Models"
      subtitle={
        promptText.length > 100
          ? promptText.substring(0, 100) + '...'
          : promptText
      }
      onClose={onClose}
      maxWidth="700px"
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#8B8580' }}>
          Loading model configs...
        </div>
      ) : (
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
        >
          {error && (
            <div
              style={{
                padding: '0.75rem 1rem',
                background: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: '8px',
                color: '#DC2626',
                fontSize: '0.875rem',
              }}
            >
              {error}
            </div>
          )}

          {/* Info about existing results */}
          {existingConfigIds.length > 0 && (
            <div
              style={{
                padding: '0.75rem 1rem',
                background: '#F9F8F7',
                borderRadius: '8px',
                fontSize: '0.875rem',
                color: '#6B6560',
              }}
            >
              {existingConfigIds.length} model
              {existingConfigIds.length !== 1 ? 's' : ''} already executed.
              Selecting them will replace existing results.
            </div>
          )}

          {/* All configs */}
          <ConfigSelector
            configs={configs}
            selectedConfigIds={selectedConfigIds}
            onSelectionChange={setSelectedConfigIds}
            defaultFilter="enabled"
          />

          {/* Actions */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.75rem',
              paddingTop: '0.5rem',
              borderTop: '1px solid #E8E3DD',
            }}
          >
            <Button variant="secondary" onClick={onClose} disabled={executing}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleExecute}
              disabled={executing || selectedConfigIds.length === 0}
              loading={executing}
            >
              {selectedWithResults > 0
                ? `Execute ${selectedConfigIds.length} (${selectedWithResults} replacing)`
                : `Execute ${selectedConfigIds.length} Model${selectedConfigIds.length !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
